import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ContentManagement() {
  const [activeTab, setActiveTab] = useState("announcements");
  const [announcements, setAnnouncements] = useState([]);
  const [resources, setResources] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  
  // New announcement form
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    is_active: true
  });
  
  // Email form
  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: "",
    send_to: "subscribed"
  });
  const [emailSending, setEmailSending] = useState(false);
  
  // Password reset form
  const [resetEmail, setResetEmail] = useState("");
  const [resetResult, setResetResult] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (activeTab === "announcements") {
        const res = await axios.get(`${API}/admin/announcements`, { headers });
        setAnnouncements(res.data.announcements || []);
      } else if (activeTab === "resources") {
        const res = await axios.get(`${API}/resources`, { headers });
        setResources(res.data.resources || []);
      } else if (activeTab === "curriculum") {
        const res = await axios.get(`${API}/admin/content/curriculum`, { headers });
        setCurriculum(res.data.curriculum || []);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
        setMessage("Network Error: Unable to connect to server. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/announcements`, newAnnouncement, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setMessage("Announcement created successfully!");
      setNewAnnouncement({ title: "", content: "", is_active: true });
      fetchData();
    } catch (err) {
      console.error("Error creating announcement:", err);
      let errorMsg = "Error creating announcement: ";
      if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
        errorMsg = "Network Error: Unable to connect to server. The production server may be temporarily unavailable.";
      } else if (err.code === "ECONNABORTED") {
        errorMsg = "Request timed out. Please try again.";
      } else if (err.response?.status === 503) {
        errorMsg = "Server temporarily unavailable. Please try again in a moment.";
      } else {
        errorMsg += (err.response?.data?.detail || err.message);
      }
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleAnnouncementStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/announcements/${id}`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
      );
      fetchData();
    } catch (err) {
      console.error("Error updating announcement:", err);
      setMessage("Error updating announcement. Please try again.");
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error("Error deleting announcement:", err);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setEmailSending(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/email-students`, emailForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(`Email sent to ${res.data.sent} students! (${res.data.failed} failed)`);
      setEmailForm({ subject: "", message: "", send_to: "subscribed" });
    } catch (err) {
      setMessage("Error sending email: " + (err.response?.data?.detail || err.message));
    } finally {
      setEmailSending(false);
    }
  };

  const generateResetToken = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    
    setResetLoading(true);
    setResetResult(null);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/generate-reset-token?email=${encodeURIComponent(resetEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResetResult(res.data);
      setMessage(`Reset token generated for ${resetEmail}`);
    } catch (err) {
      setMessage("Error: " + (err.response?.data?.detail || err.message));
      setResetResult(null);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="card" data-testid="content-management">
      <h2 style={{ color: "#FFD700", marginBottom: "20px" }}>
        Content Management
      </h2>

      {/* Tab Navigation */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
        {[
          { id: "announcements", label: "Announcements", icon: "📢" },
          { id: "email", label: "Email Students", icon: "📧" },
          { id: "password-reset", label: "Password Reset", icon: "🔑" },
          { id: "resources", label: "Resources", icon: "📚" },
          { id: "curriculum", label: "Curriculum", icon: "📖" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            style={{
              padding: "10px 20px",
              background: activeTab === tab.id ? "#FFD700" : "#333",
              color: activeTab === tab.id ? "#000" : "#EEEEEE",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              marginTop: 0
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {message && (
        <div style={{
          padding: "15px",
          background: message.includes("Error") ? "#4d1a1a" : "#1a4d1a",
          color: message.includes("Error") ? "#FF6B6B" : "#90EE90",
          borderRadius: "6px",
          marginBottom: "20px"
        }}>
          {message}
        </div>
      )}

      {/* Announcements Tab */}
      {activeTab === "announcements" && (
        <div>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>Create Announcement</h3>
          <form onSubmit={createAnnouncement}>
            <input
              type="text"
              placeholder="Announcement Title"
              value={newAnnouncement.title}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
              required
              style={{ marginBottom: "10px" }}
              data-testid="announcement-title"
            />
            <textarea
              placeholder="Announcement Content..."
              value={newAnnouncement.content}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
              required
              rows={4}
              style={{ 
                width: "100%", 
                padding: "12px", 
                background: "#222", 
                border: "2px solid #333",
                borderRadius: "6px",
                color: "#EEEEEE",
                marginBottom: "10px"
              }}
              data-testid="announcement-content"
            />
            <label style={{ display: "flex", alignItems: "center", gap: "10px", color: "#CCCCCC", marginBottom: "15px" }}>
              <input
                type="checkbox"
                checked={newAnnouncement.is_active}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, is_active: e.target.checked})}
                style={{ width: "auto" }}
              />
              Publish immediately
            </label>
            <button type="submit" disabled={loading} data-testid="create-announcement-btn">
              {loading ? "Creating..." : "Create Announcement"}
            </button>
          </form>

          <h3 style={{ color: "#87CEEB", margin: "30px 0 15px" }}>Existing Announcements</h3>
          {announcements.length === 0 ? (
            <p style={{ color: "#999" }}>No announcements yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {announcements.map((ann) => (
                <div key={ann.id} style={{
                  background: "#222",
                  padding: "15px",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${ann.is_active ? "#90EE90" : "#FF6B6B"}`
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", flexWrap: "wrap", gap: "10px" }}>
                    <div>
                      <h4 style={{ color: "#FFD700", margin: "0 0 5px" }}>{ann.title}</h4>
                      <p style={{ color: "#CCCCCC", margin: "0 0 10px", fontSize: "0.9rem" }}>{ann.content}</p>
                      <span style={{ 
                        fontSize: "0.75rem", 
                        color: ann.is_active ? "#90EE90" : "#FF6B6B",
                        background: ann.is_active ? "#1a3a1a" : "#3a1a1a",
                        padding: "3px 8px",
                        borderRadius: "4px"
                      }}>
                        {ann.is_active ? "Active" : "Hidden"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => toggleAnnouncementStatus(ann.id, ann.is_active)}
                        style={{
                          padding: "6px 12px",
                          background: "#333",
                          color: "#EEEEEE",
                          fontSize: "0.85rem",
                          marginTop: 0
                        }}
                      >
                        {ann.is_active ? "Hide" : "Show"}
                      </button>
                      <button
                        onClick={() => deleteAnnouncement(ann.id)}
                        style={{
                          padding: "6px 12px",
                          background: "#4d1a1a",
                          color: "#FF6B6B",
                          fontSize: "0.85rem",
                          marginTop: 0
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Email Students Tab */}
      {activeTab === "email" && (
        <div>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>Send Email to Students</h3>
          <form onSubmit={sendEmail}>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ color: "#CCCCCC", display: "block", marginBottom: "8px" }}>Send to:</label>
              <select
                value={emailForm.send_to}
                onChange={(e) => setEmailForm({...emailForm, send_to: e.target.value})}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#222",
                  border: "2px solid #333",
                  borderRadius: "6px",
                  color: "#EEEEEE"
                }}
                data-testid="email-send-to"
              >
                <option value="subscribed">Subscribed Students Only</option>
                <option value="trial">Trial Users Only</option>
                <option value="all">All Students</option>
              </select>
            </div>
            <input
              type="text"
              placeholder="Email Subject"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
              required
              style={{ marginBottom: "10px" }}
              data-testid="email-subject"
            />
            <textarea
              placeholder="Email Message (HTML supported)..."
              value={emailForm.message}
              onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
              required
              rows={6}
              style={{ 
                width: "100%", 
                padding: "12px", 
                background: "#222", 
                border: "2px solid #333",
                borderRadius: "6px",
                color: "#EEEEEE",
                marginBottom: "15px"
              }}
              data-testid="email-message"
            />
            <button 
              type="submit" 
              disabled={emailSending}
              style={{ background: "#1a4d1a", color: "#90EE90" }}
              data-testid="send-email-btn"
            >
              {emailSending ? "Sending..." : "Send Email to Students"}
            </button>
          </form>
        </div>
      )}

      {/* Password Reset Tab */}
      {activeTab === "password-reset" && (
        <div>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>Generate Password Reset Token</h3>
          <p style={{ color: "#CCCCCC", marginBottom: "20px" }}>
            If a student cannot receive the password reset email, you can generate a token manually and provide it to them.
          </p>
          <form onSubmit={generateResetToken}>
            <input
              type="email"
              placeholder="Student's Email Address"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              style={{ marginBottom: "15px" }}
              data-testid="reset-email-input"
            />
            <button 
              type="submit" 
              disabled={resetLoading}
              style={{ background: "#FFD700", color: "#000" }}
              data-testid="generate-reset-btn"
            >
              {resetLoading ? "Generating..." : "Generate Reset Token"}
            </button>
          </form>

          {resetResult && (
            <div style={{
              marginTop: "25px",
              padding: "20px",
              background: "#1a3a1a",
              borderRadius: "10px",
              border: "2px solid #90EE90"
            }}>
              <h4 style={{ color: "#90EE90", marginBottom: "15px" }}>Reset Token Generated</h4>
              <div style={{
                background: "#0a0a0a",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
                marginBottom: "15px"
              }}>
                <p style={{ color: "#999", marginBottom: "5px", fontSize: "0.85rem" }}>Reset Token:</p>
                <p style={{ 
                  color: "#FFD700", 
                  fontSize: "2rem", 
                  fontWeight: "bold", 
                  letterSpacing: "4px",
                  fontFamily: "monospace",
                  margin: 0
                }}>
                  {resetResult.reset_token}
                </p>
              </div>
              <div style={{ color: "#CCCCCC", fontSize: "0.9rem" }}>
                <p><strong>Username:</strong> {resetResult.username}</p>
                <p><strong>Expires in:</strong> {resetResult.expires_in}</p>
                <p style={{ marginTop: "15px", color: "#87CEEB" }}>
                  <strong>Instructions for the student:</strong>
                </p>
                <ol style={{ marginLeft: "20px", lineHeight: "1.8" }}>
                  <li>Go to the login page</li>
                  <li>Click "Forgot Password?"</li>
                  <li>Enter their email address</li>
                  <li>Enter the reset token: <strong style={{ color: "#FFD700" }}>{resetResult.reset_token}</strong></li>
                  <li>Create a new password (minimum 8 characters)</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === "resources" && (
        <div>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>Manage Resources</h3>
          <p style={{ color: "#CCCCCC", marginBottom: "20px" }}>
            Resources can be managed from the Resources section in the main navigation.
          </p>
          {loading ? (
            <p style={{ color: "#999" }}>Loading resources...</p>
          ) : (
            <div>
              <p style={{ color: "#FFD700" }}>Total Resources: {resources.length}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "15px" }}>
                {resources.slice(0, 10).map((res, idx) => (
                  <div key={idx} style={{
                    background: "#222",
                    padding: "10px 15px",
                    borderRadius: "6px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ color: "#EEEEEE" }}>{res.title}</span>
                    <span style={{ 
                      color: "#87CEEB", 
                      fontSize: "0.8rem",
                      background: "#1a2a3a",
                      padding: "3px 8px",
                      borderRadius: "4px"
                    }}>
                      {res.resource_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Curriculum Tab */}
      {activeTab === "curriculum" && (
        <div>
          <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>Manage Curriculum</h3>
          <p style={{ color: "#CCCCCC", marginBottom: "20px" }}>
            View and edit generated curriculum content.
          </p>
          {loading ? (
            <p style={{ color: "#999" }}>Loading curriculum...</p>
          ) : (
            <div>
              <p style={{ color: "#FFD700" }}>Total Subjects: {curriculum.length}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "15px" }}>
                {curriculum.map((curr, idx) => (
                  <div key={idx} style={{
                    background: "#222",
                    padding: "15px",
                    borderRadius: "6px",
                    borderLeft: "4px solid #FFD700"
                  }}>
                    <h4 style={{ color: "#FFD700", margin: "0 0 5px" }}>
                      {curr.subject_name || curr.subject_id}
                    </h4>
                    <p style={{ color: "#999", fontSize: "0.85rem", margin: 0 }}>
                      Grade Levels: {Object.keys(curr.grade_levels || {}).join(", ") || "N/A"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContentManagement;
